import { FunctionComponent, useEffect, useState } from 'react';
import { oneLine } from 'common-tags';

import { Facebook, Twitter, WhatsApp } from '@/icons';
import { UnderlineTag } from '@/atoms';
import { Prose, RecipeFeatures, SocialMedia } from '@/molecules';

import { TypeRecipeArticleProps } from './types';
import NewsletterBanner from '@/atoms/newsletter-banner';
import {
  Size as SizeBlog,
  Variation as VariationBlog,
} from '@/molecules/card-blog/types';
import { CardBlog, SliderProduct } from '@/molecules';
import { Link } from '@/atoms';
import { getUrlFromMedia } from '@/utils/utils';
import { BreadCrumbs } from '@/components/layouts/breadcrumbs';
import { ProductBlock } from '@/organisms';

export const RecipeArticle: FunctionComponent<TypeRecipeArticleProps> = ({
  className = '',
  id,
  displayBullets,
  content,
  relatedRecipes
}: TypeRecipeArticleProps) => {
  const [url, setUrl] = useState('');
  useEffect(() => {
    setUrl(document.URL);
  }, []);
  const date = content.changed
    ? new Date(content.changed).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
    : new Date().toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

  return (
    <div
      className={oneLine`
      flex flex-col gap-6 md:gap-12 
      ${className}
    `}
      id={id}
    >
      <UnderlineTag position="left" theme="light">
        Recetas
      </UnderlineTag>

      <BreadCrumbs content={content} />

      <h1 className="text-headline text-headline--h2">{content.title}</h1>

      <div className='flex flex-col sm:flex-row justify-between gap-6 md:gap-12'>
        <div className="flex flex-col justify-evenly gap-4 text-body text-body--m w-full">
          <div className='flex flex-col gap-4'>
            <span>{date}</span>
            {content.field_reading_time && (
              <span className="text-text-secondary">{content.field_reading_time} min de lectura</span>
            )}
          </div>
          <RecipeFeatures difficulty={content.field_difficulty} servings={content.field_servings} time={content.field_cooking_time} />
          <SocialMedia
            title="COMPÁRTELO"
            socialMedias={[
              {
                Icon: Facebook,
                href: `https://www.facebook.com/sharer.php?u=${url}`,
              },
              {
                Icon: Twitter,
                href: `https://twitter.com/intent/tweet?url=${url}&text=${content.title}`,
              },
              {
                Icon: WhatsApp,
                href: `https://api.whatsapp.com/send?text=${content.title}%20${url}`,
              },
            ]}
            isColumn
            variant="gray"
            position="start"
          />
        </div>
        <NewsletterBanner />
      </div>

      <Prose html={content.field_initial_text} className="text-[large] md:text-base md:leading-[28px]" />

      <div className='prose text-[large] md:text-base md:leading-[28px] text-black'>
        <h2><strong>Ingredientes:</strong></h2>
        <ul>
          {content.field_ingredients.map((value, index) => (
            <li key={`ingredients-${index}`}>
              <Prose className='text-[large] md:text-base md:leading-[28px] mt-0 mb-0' html={value} />
            </li>
          ))}
        </ul>
        {
          content.field_extra_ingredients.length ?
            content.field_extra_ingredients.map((ingredientList) => (
              <>
                <h3 className='mt-0'><strong>{ingredientList.field_title}</strong></h3>
                <ul>
                  {ingredientList.field_texts.map((value, index) => (
                    <li key={`ingredients-${index}`}>
                      <Prose className='text-[large] md:text-base md:leading-[28px] mt-0 mb-0' html={value} />
                    </li>
                  ))}
                </ul>
              </>
            )) : null
        }
      </div>
      <div className='prose text-[large] md:text-base md:leading-[28px] text-black max-w-full'>
        <h2><strong>Elaboración:</strong></h2>
        {
          displayBullets ?
            <ol>
              {
                content.field_preparation.map((value, index) => (
                  <li key={`preparation-${index}`}><Prose html={value} className="text-[large] md:text-base md:leading-[28px] mt-0 mb-0" /></li>
                ))
              }
            </ol>
            :
            <>
              {
                content.field_preparation.map((value, index) => (
                  <Prose html={value} key={`preparation-${index}`} className="text-[large] md:text-base md:leading-[28px] mt-0 mb-0" />
                ))
              }
            </>
        }
      </div>

      <Prose html={content.field_final_text} className="text-[large] md:text-base md:leading-[28px]" />
      {
        relatedRecipes != undefined ?
          <ProductBlock
            title="Recetas relacionadas"
          >
            <SliderProduct
              perPage={3}
              breakpoints={{
                1024: {
                  perPage: 3,
                },
                768: {
                  padding: 45,
                  perPage: 2,
                },
                500: {
                  padding: 45,
                  perPage: 1,
                },
              }}
            >
              {relatedRecipes.map((post: any, index: number) => (
                <CardBlog
                  image={{ src: getUrlFromMedia(post.field_image) }}
                  size={SizeBlog.MEDIUM}
                  variation={VariationBlog.DEFAULT}
                  key={index}
                >
                  <CardBlog.Title>
                    <Link href={post.path.alias}>
                      <h3>{post.title}</h3>
                    </Link>
                  </CardBlog.Title>
                  {post.field_summary && (
                    <CardBlog.Description>
                      {post.field_summary}
                    </CardBlog.Description>
                  )}
                  <CardBlog.Link href={post.path.alias}>Ver más</CardBlog.Link>
                </CardBlog>
              ))}
            </SliderProduct>
          </ProductBlock>
          : null

      }

      <hr className="text-secondary-200" />

      <SocialMedia
        title="COMPÁRTELO"
        socialMedias={[
          {
            Icon: Facebook,
            href: `https://www.facebook.com/sharer.php?u=${url}`,
          },
          {
            Icon: Twitter,
            href: `https://twitter.com/intent/tweet?url=${url}&text=${content.title}`,
          },
          {
            Icon: WhatsApp,
            href: `https://api.whatsapp.com/send?text=${content.title}%20${url}`,
          },
        ]}
        isColumn
        variant="gray"
        position="start"
      />

    </div >
  );
};

RecipeArticle.displayName = 'RecipeArticle';
